<template>
  <footer :class="{ 'w-full': $isSsm }">
    <div
      v-if="!quest.isLocked && !quest.isFinalQuest"
      class="flex justify-between items-center w-full"
    >
      <info-text
        :text-size="32"
        :text="
          disciplineId === 0 || !quest.isActive
            ? $t('career.progressInQuestToUnlock')
            : $t('career.trackQuestToSeeInTracker')
        "
      />
      <arrow-animation border="button" position="right" :tutorial="isTutorialStep">
        <app-multi-button1
          v-if="quest.isActive && disciplineId !== 0"
          v-tippy="
            isTrackerFull &&
            !isTracked && {
              theme: 'WSM',
              content: $t('career.taskTrackerFullInfo'),
              placement: 'top',
            }
          "
          btn-theme="dark"
          btn-id="tracker-btn"
          :btn-text="setTrackerBtnText"
          :custom-left-width="22"
          :disabled="isTrackerFull && !isTracked"
          class="app-multi-button--tracker"
          @click="isTrackerFull && !isTracked ? {} : toggleTaskTracking()"
        >
          <template #rightPart>
            <app-check-box v-if="quest" :is-checked="isTracked" is-transparent-background />
          </template>
        </app-multi-button1>
      </arrow-animation>
    </div>

    <div v-if="quest.isLocked" class="flexing w-full">
      <info-text
        :text-size="36"
        class="quest-locked-text"
        :text="$t('career.finishPreviousQuest')"
      />
    </div>

    <div v-if="quest.isFinalQuest" class="flexing">
      <info-text :text-size="36" :text="$t('career.nextQuestsInFuture')" />
    </div>
  </footer>
</template>

<script lang="ts">
import ArrowAnimation from '@/components/ArrowAnimation.vue'
import InfoText from '@/components/InfoText.vue'
import { MECHANIC_CAREER } from '@/globalVariables'
import { setTasksActiveStates } from '@/helpers'
import { useTaskTrackerStore } from '@/store/pinia/career/taskTracker'
import { useDisciplineStore } from '@/store/pinia/disciplinesStore'
import { useTutorialStore } from '@/store/pinia/tutorialStore'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'
import type { CareerQuest } from '@/interfaces/Quest'
import { useCareerDetailStore } from '@/store/pinia/career/careerDetail'
import type { DisciplineMenuData } from '@/interfaces/DisciplineMenu'

export default defineComponent({
  name: 'CareerFooter',
  components: {
    InfoText,
    ArrowAnimation,
  },
  props: {
    questId: {
      type: [Number, null],
      default: null,
    },
    disciplineId: {
      type: Number,
      default: 0,
    },
    careerId: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState(useCareerDetailStore, ['getQuestById']),
    ...mapState(useTutorialStore, {
      actualStageData: 'getActualStage',
      isTutorial: 'getIsTutorial',
    }),
    ...mapState(useTaskTrackerStore, {
      isTrackerFull: 'getTaskTrackerCapacityStatus',
    }),
    ...mapState(useDisciplineStore, ['getDisciplinesMenuData']),
    quest(): CareerQuest {
      return this.getQuestById(this.questId)
    },
    isTutorialStep(): boolean {
      return this.actualStageData?.name === 'clickTrackTaskCareer'
    },
    setTrackerBtnText(): string {
      return this.isTracked ? this.$t('career.trackingQuest') : this.$t('career.trackQuest')
    },
    isTracked(): boolean {
      const disciplineTaskData = this.getDisciplinesMenuData.find(
        (d: DisciplineMenuData): boolean => d.id === this.disciplineId,
      )
      return disciplineTaskData.isTracked || false
    },
  },
  methods: {
    ...mapActions(useTaskTrackerStore, [
      'addTrackingTask',
      'removeTrackingTask',
      'loadTaskTrackerQuests',
    ]),
    ...mapActions(useDisciplineStore, ['loadDisciplinesForMenu']),
    async toggleTaskTracking(): Promise<void> {
      if (!this.isTracked) {
        await this.addTrackingTask(this.careerId)
      } else {
        await this.removeTrackingTask(this.careerId)
      }

      if (this.isTutorial) {
        const tasksIndexes = [0, 1]

        tasksIndexes.forEach((index: number): void => {
          setTasksActiveStates({ state: true, index })
        })
      }

      this.loadDisciplinesForMenu(MECHANIC_CAREER, true)
      await this.loadTaskTrackerQuests()
    },
  },
})
</script>

<style lang="scss" scoped>
footer {
  width: 99.5%;
  height: 6rem;
  display: flex;
  align-items: self-end;
  justify-content: center;
  margin-top: 1.25rem;

  .quest-locked-text {
    width: 100%;
    height: 5.5rem;
    justify-content: center;
    padding-top: 0.1rem;
    padding-bottom: 0.1rem;
    @if $isWsm {
      background: linear-gradient(
        90deg,
        rgba(103, 36, 51, 0) 0%,
        rgba(103, 36, 51, 1) 20%,
        rgba(103, 36, 51, 1) 50%,
        rgba(103, 36, 51, 1) 80%,
        rgba(103, 36, 51, 0) 100%
      );
    }
    @if $isSsm {
      background: linear-gradient(
        90deg,
        rgba(132, 30, 17, 0) 0%,
        rgba(132, 30, 17, 0.2) 20%,
        rgba(132, 30, 17, 1) 50%,
        rgba(132, 30, 17, 0.2) 80%,
        rgba(132, 30, 17, 0) 100%
      );
    }
  }
}
</style>
